import { QuillRenderer } from '@/components_deprecated/Quill/QuillRenderer'
import {
  ForumQuillEditor,
  type ForumQuillEditorRef,
} from '@/modules/forum/components/ForumQuillEditor'
import { useOnErrorAuto } from '@/utils/hooks/error-handling'
import { safeParseJson } from '@/utils/parsing'
import { useMutation } from '@apollo/client/react/hooks/useMutation'
import { useTranslate } from '@ur-solutions/react-hooks'
import type Quill from 'quill'
import Delta, { type Op } from 'quill-delta'
import { useMemo, useRef } from 'react'
import { UPDATE_ABSENCE_COMMENTS_MUTATION } from '../mutations'

interface Props {
  id: string
  content: string
  editing: boolean
  onChangeEditing(editing: boolean): void
}

export function AbsenceCommentCardContent({
  id,
  content,
  editing,
  onChangeEditing,
}: Props) {
  const quillRef = useRef<ForumQuillEditorRef>(null)
  const renderedDelta = useMemo(
    () => new Delta(safeParseJson<Op[]>(content, [])),
    [content]
  )

  const onErrorAuto = useOnErrorAuto()
  const translations = useTranslate({
    updateComment: 'absence.comment.update-comment',
  })

  const [updateCommentMutation] = useMutation(
    UPDATE_ABSENCE_COMMENTS_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: ['AbsenceComments', 'AbsenceDetails'],
      onCompleted() {
        if (!quillRef.current) return
        quillRef.current.quill?.setContents(new Delta())
      },
      onError: onErrorAuto(),
    }
  )

  function handleInitQuill(quill: Quill) {
    quill.setContents(renderedDelta)
    quill.setSelection(quill.getLength(), 0)
  }

  async function handleSubmit(nextContent: string) {
    await updateCommentMutation({
      variables: {
        id,
        input: {
          content: nextContent,
        },
      },
    })
    onChangeEditing(false)
  }

  if (editing) {
    return (
      <div className="overflow-hidden rounded-b-xl bg-white">
        <ForumQuillEditor
          ref={quillRef}
          initialContent={content}
          noAttachments={false}
          roundedTopCorners
          submitText={translations.updateComment}
          hasImage={true}
          onInit={handleInitQuill}
          onSubmit={handleSubmit}
        />
      </div>
    )
  }

  return (
    <div className="px-4 py-3 text-sm text-stone-800">
      <QuillRenderer contents={renderedDelta} />
    </div>
  )
}
